import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Paper,
  FormHelperText,
  CircularProgress,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMaintenancePlan } from '../../hooks/useMaintenancePlan';
import { useAuth } from '../../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';
import {
  MaintenancePlan,
  MaintenanceTask,
  MaintenanceFrequencyType,
  MaintenanceAlertPriority,
} from '../../types/maintenance';

interface MaintenancePlanFormProps {
  vehicleId: string;
  organizationId: string;
  plan?: MaintenancePlan;
  onSubmit: () => void;
  onCancel: () => void;
}

type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

interface TaskFormData extends Omit<MaintenanceTask, 'id' | 'planId'> {
  id?: string;
  planId?: string;
}

export const MaintenancePlanForm: React.FC<MaintenancePlanFormProps> = ({
  vehicleId,
  organizationId,
  plan,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { createCustomPlan, updatePlan, loading, error, checkAdminRole } = useMaintenancePlan(vehicleId);
  const { currentUser } = useAuth();

  const frequencyTypes = [
    { value: 'mileage', label: t('maintenance.frequency_types.mileage') },
    { value: 'time', label: t('maintenance.frequency_types.time') },
    { value: 'time_or_mileage', label: t('maintenance.frequency_types.time_or_mileage') }
  ];

  const timeUnits = [
    { value: 'days', label: t('maintenance.time_units.days') },
    { value: 'months', label: t('maintenance.time_units.months') },
    { value: 'years', label: t('maintenance.time_units.years') }
  ];

  const [name, setName] = useState(plan?.name || '');
  const [description, setDescription] = useState(plan?.description || '');
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [tasks, setTasks] = useState<TaskFormData[]>(() => initializeTasks(plan?.tasks));
  const [formErrors, setFormErrors] = useState({
    name: '',
    tasks: '',
  });

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const isAdminUser = await checkAdminRole(organizationId);
        setIsAdmin(isAdminUser);
      } catch (error) {
        console.error('Erreur lors de la vérification du rôle admin:', error);
        setIsAdmin(false);
      } finally {
        setCheckingAdmin(false);
      }
    };
    checkAdmin();
  }, [checkAdminRole, organizationId]);

  function initializeTasks(planTasks?: MaintenanceTask[]): TaskFormData[] {
    if (!planTasks) return [];
    
    return planTasks.map((task): TaskFormData => ({
      id: task.id,
      name: task.name,
      description: task.description,
      frequency: {
        type: task.frequency.type,
        mileageValue: task.frequency.mileageValue,
        timeValue: task.frequency.timeValue,
        timeUnit: task.frequency.timeUnit,
        lastCompletedMileage: task.frequency.lastCompletedMileage,
        lastCompletedDate: task.frequency.lastCompletedDate
      },
      priority: task.priority,
      notificationSettings: {
        reminderFrequency: task.notificationSettings.reminderFrequency
      },
      createdBy: task.createdBy,
      updatedBy: task.updatedBy,
      createdAt: task.createdAt,
      updatedAt: task.updatedAt
    }));
  }

  const validateForm = () => {
    const errors = {
      name: '',
      tasks: '',
    };

    if (!name.trim()) {
      errors.name = t('maintenance.plans.errors.name_required');
    }

    if (tasks.length === 0) {
      errors.tasks = t('maintenance.plans.errors.tasks_required');
    }

    // Vérifier chaque tâche
    tasks.forEach((task) => {
      if (!task.name.trim()) {
        errors.tasks = t('maintenance.plans.errors.invalid_task_name');
        return;
      }

      if (!task.frequency || !frequencyTypes.map(t => t.value).includes(task.frequency.type)) {
        errors.tasks = t('maintenance.plans.errors.invalid_frequency_type');
        return;
      }

      // Vérifier les valeurs selon le type de fréquence
      if (task.frequency.type === 'time_or_mileage') {
        if (!task.frequency.mileageValue || task.frequency.mileageValue <= 0) {
          errors.tasks = t('maintenance.plans.errors.invalid_mileage_value');
          return;
        }
        if (!task.frequency.timeValue || task.frequency.timeValue <= 0) {
          errors.tasks = t('maintenance.plans.errors.invalid_time_value');
          return;
        }
        if (!task.frequency.timeUnit) {
          errors.tasks = t('maintenance.plans.errors.invalid_time_unit');
          return;
        }
      } else if (task.frequency.type === 'mileage') {
        if (!task.frequency.mileageValue || task.frequency.mileageValue <= 0) {
          errors.tasks = t('maintenance.plans.errors.invalid_mileage_value');
          return;
        }
      } else if (task.frequency.type === 'time') {
        if (!task.frequency.timeValue || task.frequency.timeValue <= 0) {
          errors.tasks = t('maintenance.plans.errors.invalid_time_value');
          return;
        }
        if (!task.frequency.timeUnit) {
          errors.tasks = t('maintenance.plans.errors.invalid_time_unit');
          return;
        }
      }

      if (!['critical', 'moderate', 'low'].includes(task.priority)) {
        errors.tasks = t('maintenance.plans.errors.invalid_priority');
        return;
      }

      if (!task.notificationSettings ||
          typeof task.notificationSettings.reminderFrequency !== 'number' ||
          task.notificationSettings.reminderFrequency <= 0) {
        errors.tasks = t('maintenance.plans.errors.invalid_reminder');
        return;
      }
    });

    setFormErrors(errors);
    return !Object.values(errors).some((error) => error !== '');
  };

  const createEmptyTask = (): TaskFormData => {
    if (!currentUser?.id) throw new Error('User not authenticated');
    
    const now = Timestamp.now();
    return {
      name: '',
      description: '',
      frequency: {
        type: 'mileage',
        mileageValue: 1000,
        timeValue: undefined,
        timeUnit: undefined,
        lastCompletedMileage: 0,
        lastCompletedDate: Timestamp.now()
      },
      priority: 'moderate',
      notificationSettings: {
        reminderFrequency: 7,
      },
      createdBy: currentUser.id,
      updatedBy: currentUser.id,
      createdAt: now,
      updatedAt: now
    };
  };

  const handleAddTask = () => {
    if (!currentUser?.id) return;
    try {
      const newTask = createEmptyTask();
      setTasks(currentTasks => [...currentTasks, newTask]);
    } catch (error) {
      console.error('Error creating new task:', error);
    }
  };

  const handleRemoveTask = (index: number) => {
    setTasks(currentTasks => currentTasks.filter((_, i) => i !== index));
  };

  const handleTaskChange = (index: number, field: string, value: any) => {
    if (!currentUser?.id) return;

    setTasks(currentTasks => {
      const updatedTasks = [...currentTasks];
      const task = { ...updatedTasks[index] };

      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        if (parent === 'frequency') {
          task.frequency = {
            ...task.frequency,
            [child]: value
          };
        } else if (parent === 'notificationSettings') {
          task.notificationSettings = {
            ...task.notificationSettings,
            [child]: value
          };
        }
      } else {
        (task as any)[field] = value;
      }

      task.updatedBy = currentUser.id;
      task.updatedAt = Timestamp.now();
      updatedTasks[index] = task;

      return updatedTasks;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!currentUser?.id) {
      throw new Error('User not authenticated');
    }

    try {
      if (plan) {
        const updatedTasks: MaintenanceTask[] = tasks.map((task) => ({
          id: task.id || '',
          planId: plan.id,
          name: task.name,
          description: task.description,
          frequency: task.frequency,
          priority: task.priority,
          notificationSettings: task.notificationSettings,
          createdBy: task.createdBy,
          updatedBy: currentUser.id,
          createdAt: task.createdAt,
          updatedAt: Timestamp.now()
        }));

        await updatePlan(plan.id, {
          name,
          description,
          tasks: updatedTasks,
          updatedBy: currentUser.id,
          updatedAt: Timestamp.now()
        });
      } else {
        const newTasks: MaintenanceTask[] = tasks.map((task) => ({
          id: '',
          planId: '',
          name: task.name,
          description: task.description,
          frequency: task.frequency,
          priority: task.priority,
          notificationSettings: task.notificationSettings,
          createdBy: currentUser.id,
          updatedBy: currentUser.id,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        }));

        await createCustomPlan({
          vehicleId,
          organizationId,
          name,
          description,
          isAutoGenerated: false,
          tasks: newTasks,
          createdBy: currentUser.id,
          updatedBy: currentUser.id
        });
      }
      onSubmit();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du plan:', error);
    }
  };

  const renderTaskForm = (task: TaskFormData, index: number) => (
    <Paper key={index} sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <TextField
            fullWidth
            label={t('maintenance.tasks.fields.name')}
            value={task.name}
            onChange={(e) => handleTaskChange(index, 'name', e.target.value)}
            required
            sx={{ mb: 2 }}
          />
        </Grid>
        <Grid item xs={1}>
          <IconButton onClick={() => handleRemoveTask(index)} color="error">
            <DeleteIcon />
          </IconButton>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('maintenance.tasks.fields.description')}
            value={task.description}
            onChange={(e) => handleTaskChange(index, 'description', e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('maintenance.tasks.fields.frequency_type')}</InputLabel>
            <Select
              value={task.frequency.type}
              onChange={(e) => handleTaskChange(index, 'frequency.type', e.target.value)}
              label={t('maintenance.tasks.fields.frequency_type')}
            >
              {frequencyTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {(task.frequency.type === 'mileage' || task.frequency.type === 'time_or_mileage') && (
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label={t('maintenance.tasks.fields.mileage_value')}
              value={task.frequency.mileageValue || ''}
              onChange={(e) => handleTaskChange(index, 'frequency.mileageValue', parseInt(e.target.value))}
              InputProps={{
                endAdornment: (
                  <Typography variant="body2" color="textSecondary">
                    km
                  </Typography>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </Grid>
        )}

        {(task.frequency.type === 'time' || task.frequency.type === 'time_or_mileage') && (
          <>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={t('maintenance.tasks.fields.time_value')}
                value={task.frequency.timeValue || ''}
                onChange={(e) => handleTaskChange(index, 'frequency.timeValue', parseInt(e.target.value))}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('maintenance.tasks.fields.time_unit')}</InputLabel>
                <Select
                  value={task.frequency.timeUnit || ''}
                  onChange={(e) => handleTaskChange(index, 'frequency.timeUnit', e.target.value)}
                  label={t('maintenance.tasks.fields.time_unit')}
                >
                  {timeUnits.map(unit => (
                    <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t('maintenance.tasks.fields.priority')}</InputLabel>
            <Select
              value={task.priority}
              onChange={(e) => handleTaskChange(index, 'priority', e.target.value)}
              label={t('maintenance.tasks.fields.priority')}
            >
              <MenuItem value="critical">{t('maintenance.priority.critical')}</MenuItem>
              <MenuItem value="moderate">{t('maintenance.priority.moderate')}</MenuItem>
              <MenuItem value="low">{t('maintenance.priority.low')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label={t('maintenance.tasks.fields.reminder_frequency')}
            value={task.notificationSettings.reminderFrequency}
            onChange={(e) =>
              handleTaskChange(
                index,
                'notificationSettings.reminderFrequency',
                parseInt(e.target.value)
              )
            }
            InputProps={{
              endAdornment: (
                <Typography variant="body2" color="textSecondary">
                  {t('maintenance.time_units.days')}
                </Typography>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  if (checkingAdmin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box p={3}>
        <Typography color="error" variant="h6" gutterBottom>
          {t('common.errors.adminRequired')}
        </Typography>
        <Typography color="text.secondary">
          {t('maintenance.plans.errors.adminOnlyFeature')}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Button onClick={onCancel}>
            {t('common.back')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <DialogTitle>
        {plan ? t('maintenance.plans.edit.title') : t('maintenance.plans.create.title')}
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('maintenance.plans.fields.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('maintenance.plans.fields.description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('maintenance.plans.fields.tasks')}
                </Typography>
                {formErrors.tasks && (
                  <FormHelperText error>{formErrors.tasks}</FormHelperText>
                )}
              </Box>

              {tasks.map((task, index) => renderTaskForm(task, index))}

              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTask}
                variant="outlined"
                sx={{ mt: 2 }}
              >
                {t('maintenance.tasks.add')}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading
                ? t('common.saving')
                : plan
                ? t('common.save')
                : t('common.create')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </>
  );
};
